/**
 * What changed, for players, newest first. Add an entry whenever something a player can see or feel ships:
 * write for someone playing the game, not for someone reading the code (no commit-speak, no internals).
 * Dates are the day it went live, in the maintainer's time zone, as YYYY-MM-DD.
 */
export interface Change {
  /** A short headline. */
  title: string;
  /** One or two plain sentences. */
  detail: string;
}

export interface ChangelogEntry {
  date: string;
  /** Optional label for the day, e.g. "Launch". */
  label?: string;
  changes: Change[];
}

export const CHANGELOG: readonly ChangelogEntry[] = [
  {
    date: "2026-10-18",
    changes: [
      {
        title: "Play on more than one device",
        detail: "Set up sync on your Progress page and send the link to your other phone, tablet or computer. Your stars, streak and verses stay the same everywhere, with no account. Your progress is locked with your private code before it leaves your device, so not even we can read it.",
      },
    ],
  },
  {
    date: "2026-10-16",
    changes: [
      {
        title: "Your week and your year, at a glance",
        detail: "The Progress page now shows your streak with a circle for each day of this week, and a year-long grid that gets darker the more days of each week you played. It starts filling in from today, plus the days of any streak you already had.",
      },
      {
        title: "A streak that ends when you miss a day",
        detail: "If you skip a day, your streak now shows as ended straight away, instead of keeping the old number until you next play. Your best streak is kept as before.",
      },
    ],
  },
  {
    date: "2026-10-13",
    changes: [
      {
        title: "A daily reminder to keep your streak",
        detail: "Switch on “Keep your streak” on your Progress page, or right after you solve a verse, and pick the hour. You'll get one notification when the verse is ready, and none on days you've already played. On iPhone, add the app to your home screen first.",
      },
      {
        title: "Install it like an app",
        detail: "Add Pass the Verse to your home screen and it opens full screen with its own icon, like any other app. You'll find “Keep it on your home screen” under the help button, and after you solve a verse.",
      },
      {
        title: "Opens even when you're offline",
        detail: "Without a connection the app still opens, says so plainly, and carries on by itself when you're back online. Your stars and streak stay safe on your device the whole time.",
      },
      {
        title: "Updates when you say so",
        detail: "When a new version is ready, a small “Update” prompt appears. Nothing reloads until you choose, so an update never interrupts a game.",
      },
    ],
  },
  {
    date: "2026-10-11",
    changes: [
      {
        title: "Read it in your favorite Bible version",
        detail: "Choose WEB, KJV, NASB 1995 or NIV with the small version button beside the verse number. The puzzle is exactly the same in every version, so you can switch whenever you like, even in the middle of a game.",
      },
    ],
  },
  {
    date: "2026-10-06",
    changes: [
      {
        title: "What's new is one tap away",
        detail: "The help button now has two tabs, “How to play” and “What's new”, so you can read the latest updates without leaving your game.",
      },
      {
        title: "A dot when there's something new",
        detail: "A small blue dot appears on the help button, and on “What's new”, when there's an update you haven't looked at yet.",
      },
      {
        title: "A look ahead",
        detail: "What's new now also shows what we're working toward, so you can see where Pass the Verse is headed as well as where it has been.",
      },
      {
        title: "A place to see what's new",
        detail: "A new “What's new” list shows every change to Pass the Verse by date, newest first. Open it from the help button, or from the link at the bottom of the progress and archive pages.",
      },
      {
        title: "Names come out of the ink once you solve it",
        detail: "When you find a verse, the verse at the top and your verse in Round 3 now show the real names. They stay hidden while you play.",
      },
      {
        title: "Sharing keeps the names hidden",
        detail: "Your shared result always shows names inked out, even if you used Reveal names while playing, so you never give the puzzle away.",
      },
    ],
  },
  {
    date: "2026-10-02",
    changes: [
      {
        title: "Psalms are named the way Psalms are named",
        detail: "One psalm is “Psalm 23” or “Psalm 129:6-7”. The whole book, or several psalms together, is “Psalms”. When you send a Psalm, the picker says “Psalm” instead of “Chapter”.",
      },
      {
        title: "A more honest button after you solve",
        detail: "Tomorrow's verse isn't out yet, so the button now says “Play previous verses” when it takes you to an earlier day.",
      },
    ],
  },
  {
    date: "2026-09-30",
    changes: [
      { title: "Correct answers are Evangel.chat blue", detail: "A correct choice now fills with the same blue as Evangel.chat." },
      {
        title: "No more verse numbers in Round 3",
        detail: "The verses around yours aren't numbered any more. A gap in the numbers (1, 2, 5, 6…) gave away where yours belonged.",
      },
      { title: "A cleaner link preview", detail: "The picture that appears when you paste a Pass the Verse link into a chat app is simpler." },
      { title: "A friendlier message when you send too fast", detail: "If you try to send a lot of verses at once, you're asked to wait a few seconds instead of seeing an error." },
    ],
  },
  {
    date: "2026-09-29",
    label: "Launch",
    changes: [
      {
        title: "Pass the Verse is open",
        detail: "One verse a day: find where it lives in the Bible. First choose the book, then the section, then slide the verse into its exact place. Game №1 is dated September 27.",
      },
      { title: "Hints, stars and streaks", detail: "Harder verses come with hints. Earn up to five stars a game, keep a streak going, and see it all on your progress screen." },
      { title: "Ink blots", detail: "Personal names are inked out of the verse. Reveal names whenever you like; it makes the verse easier to place." },
      {
        title: "Send a Verse",
        detail: "Pick a verse and one of ten short messages, then pass the link on. Your friend plays to find it and then reads your message. Links last 30 days, and a preview shows who sent it without giving the verse away.",
      },
      { title: "Light and dark", detail: "Follows your device, or choose either. Scripture is the World English Bible, which is in the public domain." },
    ],
  },
];

/**
 * Where we're headed. These are directions we're working toward, not promises: no dates, no commitments, and the order
 * means nothing. Keep them honest (things we genuinely want to do) and keep dates and "coming soon" language out.
 */
export const UPCOMING: readonly Change[] = [
  {
    title: "A question to talk about",
    detail: "After you find a verse, a short question to start a conversation about it with a friend or a family member.",
  },
  {
    title: "Play together",
    detail: "A way for a small group, a table, or a class to find the same verse at the same time.",
  },
  {
    title: "A map of the Bible",
    detail: "A picture of the books you've visited and the ones you haven't, built from the verses you've found.",
  },
  {
    title: "Seasons and themes",
    detail: "Short runs of verses gathered around a season such as Advent or Lent, or around a theme.",
  },
  {
    title: "Your progress on every device",
    detail: "An optional way to carry your streak and stars between your phone and your computer. Today they live in one browser.",
  },
  {
    title: "A careful read of every note",
    detail: "A fresh review of the background notes you see after solving a verse, checking each one against the text.",
  },
];

/** The newest entry's date: what a player has "caught up" with once they've opened the changelog. */
export const LATEST_CHANGE_DATE: string = CHANGELOG[0]!.date;

/** "11 improvements in 3 updates since we opened": the launch day itself doesn't count as an improvement. */
export function sinceLaunch(entries: readonly ChangelogEntry[] = CHANGELOG): { improvements: number; updates: number } {
  const after = entries.filter((e) => e.label !== "Launch");
  return { improvements: after.reduce((n, e) => n + e.changes.length, 0), updates: after.length };
}

/**
 * Should the "new" dot show? `first-visit` means someone brand new (nothing played, nothing sent): everything is new to them, so
 * we quietly mark it seen rather than greeting them with a dot. People who played before this page existed see the dot.
 */
export function changelogStatus(i: { seen: string | null; latest: string; hasHistory: boolean }): "new" | "seen" | "first-visit" {
  if (i.seen === null) return i.hasHistory ? "new" : "first-visit";
  return i.seen < i.latest ? "new" : "seen";
}

/** "October 6, 2026". Parsed as a local date so it can't slip a day across time zones. */
export function formatChangelogDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}
